import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext, type DemoUser } from '@/contexts/UserContext';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useOUs, useOUTree } from '@/hooks/useOUs';
import type { OUNode } from '@/data/mockOUs';
import { loadSlots, loadActiveSlotIndex, saveActiveSlotIndex } from '@/demo/demoStorage';
import type { DemoSlot } from '@/demo/demoTypes';
import { useQueryClient } from '@tanstack/react-query';

// ─── Cascade ─────────────────────────────────────────────────────────────────
// Org structure is OU → DO → BU → Departamento. Level 1 (OU) comes from the real/mocked
// catalog via useOUs(); levels 2-4 come from that OU's tree via useOUTree(ouId), same pattern
// StepAudiencia.tsx uses for the audience picker. No hardcoded mock tree — both hooks already
// switch between the real API and USE_MOCK internally, so this panel stays a UI-only simulator
// (no endpoint call of its own) without feeding fake data that looks like a real tenant's OUs.

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLE_COLOR: Record<UserRole, string> = {
  Employee:    'var(--cp-green)',
  AdminOU:     'var(--cp-cyan)',
  AdminGlobal: 'var(--cp-purple)',
};

const ROLE_ICON: Record<UserRole, string> = {
  Employee:    'fa-user',
  AdminOU:     'fa-user-shield',
  AdminGlobal: 'fa-user-tie',
};

function getInitials(nombre: string): string {
  if (!nombre.trim()) return '??';
  return nombre
    .split(' ')
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function UserConfigFAB() {
  const { t } = useTranslation();
  const { demoUser, setDemoUser, detectRoleByEmail, findMockUser } = useUserContext();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<DemoUser>({ ...demoUser });
  const [detectedRole, setDetectedRole] = useState<UserRole | null>(null);
  const [roleNotFound, setRoleNotFound] = useState(false);

  const { data: ous = [] } = useOUs();
  const { data: ouTree } = useOUTree(form.ou || null);

  const [slots] = useState<DemoSlot[]>(() => loadSlots());
  const [activeSlotIndex, setActiveSlotIndex] = useState(() => loadActiveSlotIndex());

  function switchToSlot(index: number) {
    const slot = slots[index];
    if (!slot) return;
    setActiveSlotIndex(index);
    saveActiveSlotIndex(index);
    // Sync the main demoUser with the slot's profile so role/tenant/etc. are consistent
    const updated: DemoUser = {
      ...demoUser,
      nombre: slot.nombre,
      correo: slot.correo,
      userId: slot.userId,
      ou: slot.ouId,
      tenant: slot.tenant,
    };
    setDemoUser(updated);
    setForm({ ...updated });
    void qc.invalidateQueries({ queryKey: ['pendingInstances'] });
    void qc.invalidateQueries({ queryKey: ['dashboardStats'] });
    void qc.invalidateQueries({ queryKey: ['currentScore'] });
  }


  function openModal() {
    setForm({ ...demoUser });
    setDetectedRole(null);
    setRoleNotFound(false);
    setOpen(true);
  }

  function handleEmailBlur() {
    const email = form.correo.trim();
    if (!email) return;
    const role = detectRoleByEmail(email);
    if (role) {
      setDetectedRole(role);
      setRoleNotFound(false);
      const match = findMockUser(email);
      // MOCK_USERS[].ou stores a display label (e.g. "México"), not the real OU key that
      // the select/useOUTree now expect — resolve it against the fetched catalog before use.
      const matchedOu = match?.ou ? ous.find(candidate => candidate.label === match.ou)?.key : undefined;
      setForm(prev => ({
        ...prev,
        role,
        nombre: prev.nombre || match?.name || prev.nombre,
        ou:     prev.ou     || matchedOu   || prev.ou,
      }));
    } else {
      setDetectedRole(null);
      setRoleNotFound(true);
      setForm(prev => ({ ...prev, role: 'Employee' }));
    }
  }

  function handleSave() {
    setDemoUser(form);
    setOpen(false);
  }

  /** Cascade: change OU → reset downstream levels */
  function handleOUChange(value: string) {
    setForm(prev => ({ ...prev, ou: value, do_: '', bu: '', departamento: '' }));
  }
  /** Cascade: change DO → reset BU + Dept */
  function handleDOChange(value: string) {
    setForm(prev => ({ ...prev, do_: value, bu: '', departamento: '' }));
  }
  /** Cascade: change BU → reset Dept */
  function handleBUChange(value: string) {
    setForm(prev => ({ ...prev, bu: value, departamento: '' }));
  }

  /** Generic text/email input bound to form[key] */
  function textField(
    key: keyof DemoUser,
    labelKey: string,
    placeholderKey: string,
    type: string = 'text',
  ) {
    return (
      <div className="ucfab-field">
        <label className="ucfab-label">{t(labelKey)}</label>
        <input
          className="ucfab-input"
          type={type}
          value={form[key] as string}
          placeholder={t(placeholderKey)}
          onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
          onBlur={key === 'correo' ? handleEmailBlur : undefined}
        />
      </div>
    );
  }

  /** Select bound to form[key] with a list of OU-tree options (value = key, display = label) */
  function selectField(
    labelKey: string,
    value: string,
    options: OUNode[],
    onChange: (v: string) => void,
    disabled: boolean = false,
  ) {
    return (
      <div className="ucfab-field">
        <label className="ucfab-label">{t(labelKey)}</label>
        <select
          className={`ucfab-input ucfab-select${disabled ? ' ucfab-select-disabled' : ''}`}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">—</option>
          {options.map(opt => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  const initials  = getInitials(demoUser.nombre);
  const roleColor = ROLE_COLOR[demoUser.role];

  const doOptions:   OUNode[] = ouTree?.children ?? [];
  const doNode = doOptions.find(node => node.key === form.do_);
  const buOptions:   OUNode[] = doNode?.children ?? [];
  const buNode = buOptions.find(node => node.key === form.bu);
  const deptOptions: OUNode[] = buNode?.children ?? [];

  const ROLE_LABEL: Record<UserRole, string> = {
    Employee:    t('roles.employee'),
    AdminOU:     t('roles.adminOU'),
    AdminGlobal: t('roles.adminGlobal'),
  };

  return (
    <>
      {/* FAB button */}
      <button
        className="ucfab-btn"
        title={t('userConfig.fabTitle')}
        onClick={openModal}
        style={{ borderColor: roleColor }}
        aria-label={t('userConfig.fabTitle')}
      >
        <span className="ucfab-initials" style={{ color: roleColor }}>{initials}</span>
        <span className="ucfab-role-dot" style={{ background: roleColor }} />
      </button>

      {/* Modal overlay */}
      {open && (
        <div className="ucfab-overlay" onClick={() => setOpen(false)} role="dialog" aria-modal="true">
          <div className="ucfab-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="ucfab-header">
              <span className="ucfab-header-title">
                <i className="fas fa-user-circle" aria-hidden="true" />
                {t('userConfig.title')}
              </span>
              <button className="ucfab-close" onClick={() => setOpen(false)} aria-label={t('common.close')}>
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>

            <p className="ucfab-hint">
              <i className="fas fa-flask" aria-hidden="true" />
              {t('userConfig.subtitle')}
            </p>

            {/* ── Demo slot selector ────────────────────── */}
            {slots.length === 4 && (
              <div className="ucfab-slots">
                <div className="ucfab-slots-label">
                  <i className="fas fa-users" aria-hidden="true" />
                  Ver como:
                </div>
                <div className="ucfab-slots-row">
                  {slots.map((slot, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`ucfab-slot-btn${i === activeSlotIndex ? ' active' : ''}`}
                      onClick={() => switchToSlot(i)}
                      title={slot.correo || `Usuario ${i + 1}`}
                    >
                      <span className="ucfab-slot-avatar">
                        {slot.nombre.trim()
                          ? slot.nombre.trim().split(' ').slice(0,2).map(p => p[0]?.toUpperCase()).join('')
                          : `U${i + 1}`}
                      </span>
                      <span className="ucfab-slot-name">{slot.nombre || `U${i + 1}`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Profile form ──────────────────────────────── */}
            <div className="ucfab-form">
              {/* Email — triggers role auto-detection on blur */}
              {textField('correo', 'userConfig.correo', 'userConfig.emailPlaceholder', 'email')}

              {/* Role detection feedback */}
              {detectedRole && (
                <div className="ucfab-role-detected" style={{ color: ROLE_COLOR[detectedRole] }}>
                  <i className={`fas ${ROLE_ICON[detectedRole]}`} aria-hidden="true" />
                  {t('userConfig.roleDetected')}: <strong>{ROLE_LABEL[detectedRole]}</strong>
                </div>
              )}
              {roleNotFound && (
                <div className="ucfab-role-notfound">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  {t('userConfig.roleNotFound')}
                </div>
              )}

              {textField('nombre', 'userConfig.nombre', 'userConfig.namePlaceholder')}

              {/* ── Cascading org selectors ───────── */}
              <div className="ucfab-grid">
                {selectField('userConfig.ou', form.ou, ous, handleOUChange)}
                {selectField('userConfig.do_', form.do_, doOptions, handleDOChange, !form.ou)}
              </div>
              <div className="ucfab-grid">
                {selectField('userConfig.bu', form.bu, buOptions, handleBUChange, !form.do_)}
                {selectField('userConfig.departamento', form.departamento, deptOptions,
                  v => setForm(prev => ({ ...prev, departamento: v })), !form.bu)}
              </div>

              {/* ── Additional profile fields ─────── */}
              <div className="ucfab-grid">
                {textField('puesto',       'userConfig.puesto',       'userConfig.puestoPlaceholder')}
                {textField('numeronomina', 'userConfig.numeronomina', 'userConfig.nominaPlaceholder')}
              </div>
              <div className="ucfab-field">
                <label className="ucfab-label">{t('userConfig.fechaNacimiento')}</label>
                <input
                  className="ucfab-input"
                  type="date"
                  value={form.fechaNacimiento}
                  onChange={e => setForm(prev => ({ ...prev, fechaNacimiento: e.target.value }))}
                />
              </div>

              {/* Tenant selector */}
              <div className="ucfab-field">
                <label className="ucfab-label">{t('userConfig.tenant')}</label>
                <select
                  className="ucfab-input ucfab-select"
                  value={form.tenant}
                  onChange={e => setForm(prev => ({ ...prev, tenant: e.target.value as 'LATAM' | 'Europa' }))}
                >
                  <option value="LATAM">LATAM</option>
                  <option value="Europa">Europa</option>
                </select>
              </div>

              {/* Role selector (auto-set by email; can be overridden) */}
              <div className="ucfab-field">
                <label className="ucfab-label">{t('userConfig.role')}</label>
                <select
                  className="ucfab-input ucfab-select"
                  value={form.role}
                  onChange={e => {
                    setDetectedRole(null);
                    setRoleNotFound(false);
                    setForm(prev => ({ ...prev, role: e.target.value as UserRole }));
                  }}
                >
                  <option value="Employee">{t('roles.employee')}</option>
                  <option value="AdminOU">{t('roles.adminOU')}</option>
                  <option value="AdminGlobal">{t('roles.adminGlobal')}</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="ucfab-actions">
              <button className="ucfab-save" onClick={handleSave}>
                <i className="fas fa-save" aria-hidden="true" />
                {t('userConfig.save')}
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}
